import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import type { SurveyListItem } from '../types/survey.types';
import { SurveyStatusBadge } from './SurveyStatusBadge';
import { NumericCell } from './NumericCell';

export function getSurveyColumns(searchText: string | undefined): ColumnDef<SurveyListItem>[] {
  return [
    createSerialColumn<SurveyListItem>(),
    {
      accessorKey: 'id',
      header: 'Project No',
      cell: ({ row }) => <span className="tabular-nums">#{row.original.id}</span>,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.title} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      cell: ({ row }) => formatDisplayDate(row.original.startDate),
    },
    {
      accessorKey: 'endDate',
      header: 'End Date',
      cell: ({ row }) => formatDisplayDate(row.original.endDate),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <SurveyStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'questionCount',
      header: 'Questions',
      cell: ({ row }) => <NumericCell value={row.original.questionCount} />,
    },
    {
      accessorKey: 'respondentCount',
      header: 'Respondents',
      cell: ({ row }) => <NumericCell value={row.original.respondentCount} />,
    },
    {
      accessorKey: 'responseCount',
      header: 'Responses',
      cell: ({ row }) => <NumericCell value={row.original.responseCount} />,
    },
    {
      accessorKey: 'createdByName',
      header: 'Created By',
      cell: ({ row }) => (
        <WrappingCellText>{row.original.createdByName ?? <EmptyValue />}</WrappingCellText>
      ),
    },
    createActionsColumn<SurveyListItem>((survey) => (
      <RowViewLink
        to="/feedback/surveys/$surveyId"
        params={{ surveyId: survey.id }}
        aria-label={`View ${survey.title}`}
      />
    )),
  ];
}
